import { createFileRoute } from '@tanstack/react-router';
import { CheckCircle2Icon, ImageIcon, RefreshCwIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, ListSkeleton } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardBody, DataRow, SectionTitle } from '@/components/ui/card';
import { Alert } from '@/components/ui/form';
import { bytes, dateTimeId, integer, relativeId } from '@/lib/format';
import { useOnline } from '@/lib/use-online';
import { db, META_KEYS } from '@/offline/db';
import { describeOutbox, type QueueEntry } from '@/offline/queries';
import { storageStatus } from '@/offline/storage-quota';
import { retryOutboxItem, run } from '@/offline/sync-engine';
import { useOfflineQuery, useSyncState } from '@/offline/use-offline-query';

export const Route = createFileRoute('/_app/sinkronisasi')({
  component: SyncQueuePage,
});

/**
 * Antrean sinkronisasi & antrean bermasalah (T-P7-08, FR-SYNC-06).
 *
 * Dua daftar terpisah dengan alasan berbeda. Antrean biasa akan terkirim sendiri
 * dan hanya perlu dilihat; antrean bermasalah **tidak akan pernah** terkirim
 * tanpa tindakan — item yang ditolak server atau habis percobaannya. Menyatukan
 * keduanya membuat yang kedua tenggelam di antara puluhan item yang baik-baik saja.
 */
function SyncQueuePage() {
  const online = useOnline();
  const { running, lastError } = useSyncState();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const { data, loading } = useOfflineQuery(async () => {
    const items = await db.outbox.orderBy('createdAt').toArray();
    const media = await db.mediaQueue.toArray();

    return {
      queued: await describeOutbox(
        items.filter((item) => item.status === 'PENDING' || item.status === 'SENDING'),
      ),
      problems: await describeOutbox(
        items.filter((item) => item.status === 'NEEDS_ATTENTION' || item.status === 'CONFLICT'),
      ),
      mediaPending: media.filter(
        (item) => item.status === 'PENDING' || item.status === 'UPLOADING',
      ),
      mediaFailed: media.filter((item) => item.status === 'FAILED'),
      lastSyncAt: (await db.meta.get(META_KEYS.lastSyncAt))?.value as string | undefined,
      storage: await storageStatus(),
    };
  }, []);

  if (loading || !data) return <ListSkeleton rows={3} />;

  const nothingPending =
    data.queued.length === 0 && data.problems.length === 0 && data.mediaPending.length === 0;

  return (
    <div className="space-y-4 px-4 py-4">
      <Card>
        <CardBody>
          <dl className="divide-y divide-border">
            <DataRow label="Koneksi" value={online ? 'Terhubung' : 'Offline'} />
            <DataRow label="Sinkron terakhir" value={relativeId(data.lastSyncAt)} />
            <DataRow label="Menunggu kirim" value={integer(data.queued.length)} />
            <DataRow label="Foto menunggu unggah" value={integer(data.mediaPending.length)} />
            <DataRow
              label="Ruang penyimpanan"
              value={data.storage.supported ? `${bytes(data.storage.availableBytes)} tersisa` : '—'}
            />
          </dl>
        </CardBody>
      </Card>

      {lastError ? <Alert variant="danger">{lastError}</Alert> : null}

      <Button
        size="lg"
        fullWidth
        disabled={!online || running}
        onClick={() => void run()}
        leading={
          <RefreshCwIcon
            className={running ? 'size-4 animate-spin' : 'size-4'}
            aria-hidden="true"
          />
        }
      >
        {running ? 'Menyinkronkan…' : 'Sinkronkan sekarang'}
      </Button>

      {data.problems.length > 0 ? (
        <section className="space-y-2">
          <SectionTitle>Antrean bermasalah</SectionTitle>
          <Alert variant="warning">
            Item berikut tidak akan terkirim sendiri. Kirim ulang setelah keadaannya berubah, atau
            hapus bila memang tidak seharusnya terkirim — data yang dihapus tidak dapat dipulihkan.
          </Alert>

          {data.problems.map((entry) => (
            <ProblemCard
              key={entry.item.clientUuid}
              entry={entry}
              confirming={confirmDelete === entry.item.clientUuid}
              onConfirm={() => setConfirmDelete(entry.item.clientUuid)}
              onCancel={() => setConfirmDelete(null)}
              onDelete={async () => {
                await db.outbox.delete(entry.item.clientUuid);
                await db.mediaQueue.where('ownerClientUuid').equals(entry.item.clientUuid).delete();
                setConfirmDelete(null);
              }}
            />
          ))}
        </section>
      ) : null}

      <section className="space-y-2">
        <SectionTitle>Menunggu kirim</SectionTitle>

        {nothingPending ? (
          <EmptyState
            icon={CheckCircle2Icon}
            title="Semua sudah terkirim"
            description="Tidak ada kunjungan, pembayaran, atau foto yang tertahan di perangkat ini."
          />
        ) : (
          <>
            {data.queued.map((entry) => (
              <Card key={entry.item.clientUuid}>
                <CardBody className="space-y-1 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{entry.title}</p>
                      <p className="truncate text-xs text-muted-foreground">{entry.subtitle}</p>
                    </div>
                    <Badge tone={entry.item.status === 'SENDING' ? 'brand' : 'neutral'}>
                      {entry.item.status === 'SENDING' ? 'Mengirim' : 'Antre'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Dibuat {dateTimeId(entry.item.createdAt)}
                    {entry.item.attempts > 0 ? ` · percobaan ke-${entry.item.attempts}` : ''}
                  </p>
                </CardBody>
              </Card>
            ))}

            {data.mediaPending.length > 0 ? (
              <Card>
                <CardBody className="flex items-center gap-3 p-3">
                  <ImageIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <p className="flex-1 text-sm">
                    {integer(data.mediaPending.length)} foto menunggu unggah
                    <span className="block text-xs text-muted-foreground">
                      {bytes(data.mediaPending.reduce((total, item) => total + item.sizeBytes, 0))}{' '}
                      · diunggah setelah kunjungannya terkirim
                    </span>
                  </p>
                </CardBody>
              </Card>
            ) : null}
          </>
        )}
      </section>

      {data.mediaFailed.length > 0 ? (
        <section className="space-y-2">
          <SectionTitle>Foto gagal diunggah</SectionTitle>
          {data.mediaFailed.map((item) => (
            <Card key={item.clientUuid}>
              <CardBody className="space-y-1 p-3">
                <p className="text-sm font-medium">{item.kind}</p>
                <p className="text-xs text-danger">{item.lastError ?? 'Gagal tanpa keterangan'}</p>
                <Button
                  variant="secondary"
                  onClick={() =>
                    void db.mediaQueue.update(item.clientUuid, {
                      status: 'PENDING',
                      attempts: 0,
                      nextAttemptAt: new Date().toISOString(),
                      lastError: null,
                    })
                  }
                >
                  Coba unggah lagi
                </Button>
              </CardBody>
            </Card>
          ))}
        </section>
      ) : null}
    </div>
  );
}

function ProblemCard({
  entry,
  confirming,
  onConfirm,
  onCancel,
  onDelete,
}: {
  entry: QueueEntry;
  confirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onDelete: () => Promise<void>;
}) {
  const conflict = entry.item.status === 'CONFLICT';

  return (
    <Card>
      <CardBody className="space-y-2 p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{entry.title}</p>
            <p className="truncate text-xs text-muted-foreground">{entry.subtitle}</p>
          </div>
          <Badge tone={conflict ? 'warning' : 'danger'}>
            {conflict ? 'Konflik' : 'Perlu tindakan'}
          </Badge>
        </div>

        <p className="text-xs text-danger">{entry.item.lastError ?? 'Ditolak server'}</p>
        <p className="text-xs text-muted-foreground">
          Dibuat {dateTimeId(entry.item.createdAt)} · {entry.item.attempts} percobaan
        </p>

        {conflict ? (
          <p className="text-xs text-muted-foreground">
            Konflik ditinjau supervisor di back office. Kirim ulang hanya bila diminta.
          </p>
        ) : null}

        {confirming ? (
          <div className="space-y-2 rounded-card border border-danger/40 bg-danger/10 p-2">
            <p className="text-xs">
              Hapus permanen? Kunjungan ini beserta fotonya akan hilang dari perangkat dan tidak
              pernah sampai ke sistem.
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" fullWidth onClick={onCancel}>
                Batal
              </Button>
              <Button variant="danger" fullWidth onClick={() => void onDelete()}>
                Hapus permanen
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => void retryOutboxItem(entry.item.clientUuid)}
              leading={<RefreshCwIcon className="size-4" aria-hidden="true" />}
            >
              Kirim ulang
            </Button>
            <Button
              variant="ghost"
              onClick={onConfirm}
              leading={<Trash2Icon className="size-4" aria-hidden="true" />}
            >
              Hapus
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
